import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { DatasourceMeasurementsDto, type DatasourceId, type NetworkId } from '../../types';
import { apiClient } from '../apiClient';
import { createQueryConfig } from '../config';

const LOOKBACK_CHART_HOURS = 72;
const generatePlaceholderData = (minVal: number) => {
  return Array.from({ length: LOOKBACK_CHART_HOURS }, () => minVal);
};

export const useGetDatasourceMeasurements = ({
  networkId,
  datasourceId,
}: {
  networkId: NetworkId;
  datasourceId: DatasourceId;
}): UseQueryResult<{ measurements: DatasourceMeasurementsDto }> => {
  const now = DateTime.utc().startOf('hour');

  const usEpaValidData = generatePlaceholderData(0);
  const ukDefraValidData = generatePlaceholderData(1);
  const waDwerValidData = generatePlaceholderData(0);
  const placeholderMeasurements: DatasourceMeasurementsDto = {
    datasourceId: datasourceId,
    times: Array.from({ length: LOOKBACK_CHART_HOURS }, (_, i) =>
      now.minus({ hours: i + 1 }).toISO()
    ).toReversed() as unknown as Date[],
    measurements: {
      pm2_5ConcMassNowcastUsEpaAqi: usEpaValidData,
      no2Conc1HourMeanUsEpaAqi: usEpaValidData,
      pm2_5ConcMass24HourRollingMeanUkDefraAqi: ukDefraValidData,
      no2Conc1HourMeanUkDefraAqi: ukDefraValidData,
      pm2_5ConcMass1HourMeanWaDwerAqi: waDwerValidData,
      no2Conc1HourMeanWaDwerAqi: waDwerValidData,
    },
  };

  return useQuery({
    queryKey: ['datasource-measurements', datasourceId],
    queryFn: async () => {
      if (!networkId || !datasourceId) {
        return {};
      }

      const { data } = await apiClient.get(`/v1/datasources/${datasourceId}/measurements`, {
        params: {
          networkId: networkId,
        },
      });

      return data.data;
    },
    placeholderData: { measurements: placeholderMeasurements },
    ...createQueryConfig(),
  });
};
